// APP-1282 (s-exceptions-checks-triage-on-019ea7f7): pure RQL composition for
// the admin rebalance list's exception-triage controls.
//
// The list has one manual RQL text input plus three OPTIONAL refinements:
//   - the "Exceptions only" toggle  → the canonical exception lens
//     `check_status != 'pass'` (R13). When on with no refinement engaged it
//     returns EVERY exception (review_recommended ∪ action_required), including
//     a high-score action_required and a score-only review_recommended — no
//     exception is hidden by default (R14/AC5).
//   - the severity chips            → the OR of `check_<sev> > 0` over the
//     ENABLED severities (R15/R16). Inactive by default; impose no clause until
//     engaged, and impose no clause when no chip is enabled.
//   - the score-under filter        → strict `check_score < N` (R17). Inactive
//     by default; threshold excluded.
//
// Every active control AND-composes with the others AND with the manual query
// (R18); each applies only while active. The server-side APP-867 RQL engine then
// executes the composed query (R9) — there is no client-side filtering.

export const SEVERITIES = ["critical", "error", "warning", "info"] as const;
export type Severity = (typeof SEVERITIES)[number];

// R15: when the chips are engaged, critical/error/warning start enabled, info
// disabled. (These are the INITIAL settings of an engaged refinement, not an
// always-applied clause — the chips are inactive until the user engages them.)
export const DEFAULT_ENABLED_SEVERITIES: Record<Severity, boolean> = {
  critical: true,
  error: true,
  warning: true,
  info: false,
};

// R17: the score-under refinement's initial threshold, independent of the
// engine's status-promotion threshold.
export const DEFAULT_SCORE_UNDER = 90;

export const EXCEPTIONS_CLAUSE = "check_status != 'pass'";

export type AdminQueryRefinements = {
  // Manual RQL the user typed (may be empty).
  manualQuery: string;
  // The "Exceptions only" toggle.
  exceptionsOnly: boolean;
  // Severity chips: engaged at all, and which severities are enabled.
  chipsEngaged: boolean;
  enabledSeverities: Record<Severity, boolean>;
  // Score-under refinement: engaged at all, and the threshold N.
  scoreUnderEngaged: boolean;
  // APP-2138/R33: `null` is an empty threshold field; it adds no term.
  scoreUnder: number | null;
};

/**
 * Build the OR-of-`check_<sev> > 0` clause for the engaged, enabled severities.
 * Returns "" when the chips are not engaged or no severity is enabled (R16) —
 * imposing no clause. A single severity yields a bare `check_<sev> > 0`; two or
 * more are parenthesised so the OR composes safely under an outer AND.
 */
export function buildSeverityClause(
  chipsEngaged: boolean,
  enabledSeverities: Record<Severity, boolean>,
): string {
  if (!chipsEngaged) return "";
  const clauses = SEVERITIES.filter((sev) => enabledSeverities[sev]).map(
    (sev) => `check_${sev} > 0`,
  );
  if (clauses.length === 0) return "";
  if (clauses.length === 1) return clauses[0];
  return `(${clauses.join(" or ")})`;
}

/**
 * AND-compose the active controls and the manual query into a single RQL string
 * (R18). Inactive controls contribute nothing (R14). Returns "" when nothing is
 * active — the server then lists the firm's rebalances unfiltered.
 *
 * Each non-empty term is parenthesised before being AND-joined so a manual
 * query containing its own `or` cannot bleed across the AND boundary.
 */
export function composeAdminQuery(refinements: AdminQueryRefinements): string {
  const {
    manualQuery,
    exceptionsOnly,
    chipsEngaged,
    enabledSeverities,
    scoreUnderEngaged,
    scoreUnder,
  } = refinements;

  const terms: string[] = [];

  const manual = manualQuery.trim();
  if (manual) terms.push(manual);

  // R13: the canonical exception lens.
  if (exceptionsOnly) terms.push(EXCEPTIONS_CLAUSE);

  // R16: severity chips, only while engaged with ≥1 severity enabled.
  const severityClause = buildSeverityClause(chipsEngaged, enabledSeverities);
  if (severityClause) terms.push(severityClause);

  // R17: strict score-under, only while engaged. APP-2138/R33: an empty
  // threshold (`null`) adds no term.
  if (scoreUnderEngaged && scoreUnder !== null)
    terms.push(`check_score < ${scoreUnder}`);

  if (terms.length === 0) return "";
  if (terms.length === 1) return terms[0];
  return terms.map((t) => `(${t})`).join(" and ");
}
